package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initTts_shared_kotlin_onnxPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("tts_shared_kotlin_onnx.md", FrontMatterElement.Builder { addScalar("title", "Neural voices in shared Kotlin: Kokoro on ONNX Runtime, without sherpa-onnx or espeak"); addScalar("description", "Part two of Parrot's on-device voices: replacing sherpa-onnx with ONNX Runtime behind one Kotlin interface, calling its C API from Kotlin on iPhone, and porting an English pronunciation front end so Kokoro needs no espeak-ng."); addScalar("date", "2026-10-20"); addScalar("cover", "/articles/tts_shared_kotlin.jpeg"); }))
}

@Page("/blog/kokoro-tts-shared-kotlin-onnx-runtime")
@Layout(".pages.MarkdownPage")
@Composable
fun Tts_shared_kotlin_onnxPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("neural-voices-in-shared-kotlin-kokoro-on-onnx-runtime-without-sherpa-onnx-or-espeak") }) {
        org.jetbrains.compose.web.dom.Text("Neural voices in shared Kotlin: Kokoro on ONNX Runtime, without sherpa-onnx or espeak")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/on-device-neural-tts-android") {
            org.jetbrains.compose.web.dom.Text("the first note on Parrot's voices")
        }
        org.jetbrains.compose.web.dom.Text(", the neural voices ran on ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/k2-fsa/sherpa-onnx") {
            org.jetbrains.compose.web.dom.Text("sherpa-onnx")
        }
        org.jetbrains.compose.web.dom.Text(", an Android library that wraps ONNX Runtime and a ready-made text-to-speech pipeline. That note ended with \"the iPhone version is next\".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The iPhone version is what made me take sherpa-onnx out. Parrot's Kokoro and Supertonic voices now run on ONNX Runtime directly, from shared Kotlin, on Android and iPhone. Kokoro gets its pronunciation from a Kotlin port of an English front end instead of espeak-ng. This note is about why, how, and what it cost.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-replace-a-library-that-worked") }) {
        org.jetbrains.compose.web.dom.Text("Why replace a library that worked")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two reasons, and the second one decided it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The iPhone.")
        }
        org.jetbrains.compose.web.dom.Text(" sherpa-onnx ships an iOS build, so the first iPhone version used it from Swift: a Swift class implemented a Kotlin interface, took text in and handed float audio back. It worked, but the voice pipeline then lived in two places, Kotlin on Android and Swift on iPhone, and nothing about the iPhone side could be tested from Kotlin.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("espeak-ng.")
        }
        org.jetbrains.compose.web.dom.Text(" Kokoro doesn't read letters. It reads phonemes, so every sentence first goes through a pronunciation front end. In sherpa-onnx, that front end is espeak-ng, which is GPL-3.0. Reading sherpa-onnx's source showed three things:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("the Kokoro English path always initialises espeak-ng, even though the model pack ships its own pronunciation dictionaries;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("when a language is given, which it is for English voices, the whole text goes to espeak-ng and the dictionaries aren't used at all;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("there's no build switch to leave espeak-ng out. Turning on text-to-speech compiles it in.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Not calling it doesn't help; the code is in the binary either way. The options were to keep it, maintain a patched fork of sherpa-onnx for four Android ABIs and iOS on every upgrade, or drop sherpa-onnx and write the small part of it Parrot actually uses. The model itself needs no espeak: it takes a list of token ids. So I took the third option.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-interface-over-onnx-runtime") }) {
        org.jetbrains.compose.web.dom.Text("One interface over ONNX Runtime")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Everything platform-specific about the neural voices is now one interface:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""/**
 * One loaded ONNX model, the only platform seam of the neural voices (Kokoro, Supertonic).
 */
interface OnnxSession : AutoCloseable {

    /** The model's custom metadata (Kokoro keeps sample_rate, n_speakers and style_dim here). */
    val metadata: Map<String, String>

    /**
     * Runs the model and returns its first output. [inputs] are positional, in the model's
     * own input order, as sherpa-onnx passed them.
     */
    fun run(inputs: List<OnnxTensorData>): OnnxTensorData.Floats

    /** Ends a [run] in progress on another thread (it then throws); nothing when none runs. */
    fun terminate() {}
}

expect fun openOnnxSession(modelPath: String, numThreads: Int): OnnxSession
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There are four implementations:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Android")
            }
            org.jetbrains.compose.web.dom.Text(" uses the official ")
            io.github.retar.portfolio.components.widgets.InlineCode("""onnxruntime-android""")
            org.jetbrains.compose.web.dom.Text(" package.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Host tests")
            }
            org.jetbrains.compose.web.dom.Text(" use ONNX Runtime's desktop jar, so the real Kokoro and Supertonic models run in ordinary unit tests on a laptop.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("iPhone")
            }
            org.jetbrains.compose.web.dom.Text(" calls ONNX Runtime's C API directly from Kotlin, through cinterop. The whole definition file is five lines:")
        }
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""package = com.retro99.onnxruntime
headers = onnxruntime_c_api.h
headerFilter = onnxruntime_*.h
""")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No Swift class, no registration at app start. The iPhone test suite can load the real runtime and synthesize real speech, and it does: 1,235 tests in that module, including real Kokoro and Supertonic runs when the voice packs are on the machine.")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The web")
            }
            org.jetbrains.compose.web.dom.Text(" throws \"not wired in the browser\" for now. The browser version of Parrot uses the browser's own voices.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Above that seam, everything is common code: the Kokoro and Supertonic pipelines, the read-aloud engine, the audio cache, chapter preparation and their tests.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("kokoro-without-the-wrapper") }) {
        org.jetbrains.compose.web.dom.Text("Kokoro without the wrapper")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Once sherpa-onnx is gone, running Kokoro turns out to be small. The model takes three inputs: the token ids with a 0 at each end, a \"style\" vector for the voice, and the speed. The style comes from ")
        io.github.retar.portfolio.components.widgets.InlineCode("""voices.bin""")
        org.jetbrains.compose.web.dom.Text(", which holds one row per voice ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("per sentence length")
        }
        org.jetbrains.compose.web.dom.Text(", so the row is picked by how many tokens the sentence has:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""internal fun run(ids: IntArray, sid: Int, speed: Float): FloatArray {
    val x = LongArray(ids.size + 2)
    for (i in ids.indices) x[i + 1] = ids[i].toLong()
    val rowStart = (sid * maxTokens + ids.size) * styleWidth
    val style = styles.copyOfRange(rowStart, rowStart + styleWidth)
    return session.run(
        listOf(
            OnnxTensorData.Longs(x, longArrayOf(1, x.size.toLong())),
            OnnxTensorData.Floats(style, longArrayOf(1, styleWidth.toLong())),
            OnnxTensorData.Floats(floatArrayOf(speed), longArrayOf(1)),
        ),
    ).data
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The table has 510 rows per voice, so longer sentences are cut into pieces of at most 508 tokens, after the last space where possible. The sample rate, speaker count and table shape come from the model's own metadata, the same way sherpa-onnx read them. I kept sherpa's silence scaling too, so the pacing of the speech didn't change.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Cancellation got simpler as well. In part one, cancelling a sentence meant a callback that native code called with each batch of samples, and a Kotlin lambda in that position crashed the app. Now a cancelled coroutine just calls ")
        io.github.retar.portfolio.components.widgets.InlineCode("""terminate()""")
        org.jetbrains.compose.web.dom.Text(" on the session, and ONNX Runtime ends the run in progress:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""internal suspend fun <T> runTerminable(context: CoroutineContext, terminate: () -> Unit, block: () -> T): T = coroutineScope {
    val watcher = launch(start = CoroutineStart.UNDISPATCHED) {
        try { awaitCancellation() } finally { terminate() }
    }
    try {
        withContext(context) { block() }
    } catch (error: Exception) {
        currentCoroutineContext().ensureActive()
        throw error
    } finally {
        watcher.cancel()
    }
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That's what lets a sentence you're waiting for interrupt a chapter being prepared in the background.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-hard-part-pronouncing-english") }) {
        org.jetbrains.compose.web.dom.Text("The hard part: pronouncing English")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kokoro v1.0 was trained on phonemes from ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/hexgrad/misaki") {
            org.jetbrains.compose.web.dom.Text("misaki")
        }
        org.jetbrains.compose.web.dom.Text(", its author's own Python front end. misaki looks words up in dictionaries and falls back to espeak-ng for words it doesn't know. So the target was clear: port misaki's rules to Kotlin, and replace the espeak fallback with something that isn't GPL.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The new module, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""lib/g2p""")
        org.jetbrains.compose.web.dom.Text(", is common Kotlin for Android, iPhone and the web. For each word it tries, in order:")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("the pack's lexicon plus misaki's extra entries (special cases like \"a\", \"the\", \"to\", and capitalised words like \"US\" and \"NASA\");")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("misaki's rules for suffixes and possessives (-s, -ed, -ing, 's);")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("the word again with accents stripped;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("the CMU Pronouncing Dictionary;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("the parts of a hyphenated compound;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("a small neural model, g2p_en's GRU encoder-decoder, ported to plain Kotlin, for words nothing else knows.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Before any of that, a text normaliser does the things espeak-ng did for free: numbers, ordinals, years (\"eighteen hundred eleven\"), decimals, currency, abbreviations like \"Mr.\" and \"No. 5\", and Roman numerals. One deliberate difference: misaki spells \"IV\" as letters, and Parrot reads \"Chapter IV\" as \"Chapter four\".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The numbers I tested against:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Coverage.")
            }
            org.jetbrains.compose.web.dom.Text(" Pride and Prejudice has 130,576 words and numbers. Before the neural fallback, 585 of them (0.45%) had no pronunciation from any dictionary or rule. With it, none.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Agreement with misaki.")
            }
            org.jetbrains.compose.web.dom.Text(" On a recorded list of words, 95.9% of comparable words match misaki exactly, and 97.8% of words inside whole sentences do. The misses are mostly heteronyms: \"live\", \"close\", \"present\", and \"I read\" in past or present. misaki tells those apart with a part-of-speech tagger; Parrot guesses from the word before.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The g2p_en port")
            }
            org.jetbrains.compose.web.dom.Text(" gives the same phonemes as the original Python on all 41 test words.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Nothing GPL or LGPL is in the module. The ported code is Apache-2.0 and CMUdict is BSD-2-Clause, each file names its source and licence in its header, and the licence texts ship with the voice pack. On the iPhone release build, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""strings""")
        org.jetbrains.compose.web.dom.Text(" finds no \"espeak-ng\" and no \"sherpa\".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There was a bonus. The old espeak path cut sentences short at \"Mr.\": one 212-character sentence came out as 1.4 seconds of audio. The new front end reads all of it, in 10.7 seconds.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("updating-the-voice-on-people-s-phones") }) {
        org.jetbrains.compose.web.dom.Text("Updating the voice on people's phones")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The pronunciation data is about 7.6 MB of new files (4.1 MB compressed): misaki's extras, CMUdict and the g2p_en weights. The 114 MB model and the 28 MB voice table didn't change, so an update downloads only the new files and hard-links the rest from the old pack.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The new front end produces different phonemes, so the same sentence sounds slightly different. The cache key for every sentence's audio includes the model version, and that now ends in ")
        io.github.retar.portfolio.components.widgets.InlineCode("""+misaki-g2p1""")
        org.jetbrains.compose.web.dom.Text(". Audio prepared by the old version is a clean cache miss instead of a mix of two pronunciations in one chapter.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-cost") }) {
        org.jetbrains.compose.web.dom.Text("What it cost")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Size.")
        }
        org.jetbrains.compose.web.dom.Text(" The Android APK grew by 1.1 MB. The official ONNX Runtime package replaced sherpa-onnx's own copy of ONNX Runtime and its JNI layer.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Speed, at first.")
        }
        org.jetbrains.compose.web.dom.Text(" On a Samsung Galaxy S24, the new Kokoro path was 15 to 25% slower per second of audio than sherpa-onnx in the first test. That test wasn't clean, though: the phone was warm and charging, and the old and new runs weren't interleaved.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the next step was a proper benchmark on three phones, a Galaxy Z Fold 3, the S24 and a Lenovo device, with 40 sentences per setting. Two findings:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Using more threads than the phone has big cores cost 30 to 40%.")
            }
            org.jetbrains.compose.web.dom.Text(" Parrot now counts the cores with at least half the capacity of the fastest one and uses that many, between 2 and 4.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Heat mattered more than any setting.")
            }
            org.jetbrains.compose.web.dom.Text(" On the S24, one cool run with 2 threads beat every 4-thread run. Pinning threads, turning off spinning, a pre-optimised model and the XNNPACK backend either didn't help or changed the audio.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The honest result: on the S24, no setting keeps Kokoro ahead of playback at normal speed for ten minutes of live reading. That's why Parrot prepares chapters ahead of time, as described in part one, and now does it more carefully while the phone is cool or charging. Supertonic keeps up comfortably.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("iPhone.")
        }
        org.jetbrains.compose.web.dom.Text(" In the iOS simulator, Kokoro generates 4 seconds of audio in about 2 seconds, a little faster than the sherpa-onnx build did in the same test. Supertonic generates 5.8 seconds in 1.2 seconds. Both now download, prepare and play in the iPhone app.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("honest-status") }) {
        org.jetbrains.compose.web.dom.Text("Honest status")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Everything on iPhone so far has run in the simulator. Speed, memory and heat on a real iPhone haven't been measured.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The run reports don't record anyone comparing the old and new Kokoro by ear. The side-by-side recordings exist, and that listening test is still to do.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Heteronyms remain a guess. \"I read the book\" is past or present depending on context, and the front end doesn't have the grammar to know.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The web version doesn't have neural voices. ONNX Runtime has a WebAssembly build, and the interface is where it would plug in.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The lesson I'd keep: a library that does everything for you also decides everything for you, including what's in your binary. Here, the part Parrot needed from sherpa-onnx was one model call and a front end. Owning those cost a module of Kotlin, and it gave back one pipeline instead of two, tests that run the real model on every platform, and a binary I can explain.")
    }
}
